export const kpiCategories: { category: string; kpis: string[] }[] = [
  {
    category: 'Security - Infrastructure Security',
    kpis: [
      'End of Life',
      'Agent Coverage',
      'Public IP',
      'Key Rotation',
      'Privileged Identity Management',
      'Java SDK Resource',
    ],
  },
  {
    category: 'Security - Code Security',
    kpis: [
      'Blackduck Coverage',
      'Veracode Coverage',
      'Coverity Coverage',
      'Criticality Set',
      'Commit Signature Verification',
    ],
  },
  {
    category: 'DORA Metrics',
    kpis: ['P75 Lead Time for Changes', 'Avg Lead Time for Changes'],
  },
  {
    category: 'Developer Productivity - Efficiency & Flow',
    kpis: [
      'P75 Time to Pickup',
      'Avg Time to Pickup',
      'P75 Time to Review',
      'Avg Time to Review',
      'P75 Time to Merge',
      'Avg Time to Merge',
      'Avg CI/CD duration',
      'CI/CD Success Rate',
      'Avg PR Size',
    ],
  },
];

export const trendDownKPIs = new Set([
  'Public IP',
  'P75 Lead Time for Changes',
  'Avg Lead Time for Changes',
  'P75 Time to Pickup',
  'Avg Time to Pickup',
  'P75 Time to Review',
  'Avg Time to Review',
  'P75 Time to Merge',
  'Avg Time to Merge',
  'Avg CI/CD duration',
  'P75 PR Size',
  'Avg PR Size',
  'Criticality Set',
]);

export const lowerIsBetterKPIs = new Set([
  'Public IP',
  'P75 Lead Time for Changes',
  'Avg Lead Time for Changes',
  'P75 Time to Pickup',
  'Avg Time to Pickup',
  'P75 Time to Review',
  'Avg Time to Review',
  'P75 Time to Merge',
  'Avg Time to Merge',
  'Avg CI/CD duration',
  'P75 PR Size',
  'Avg PR Size',
  'Criticality Set',
]);

export const normalizedTargetForItem: Record<string, number | string> = {
  // Security
  'End of Life': 100,
  'Privileged Identity Management': 100,
  'Key Rotation': 100,
  'Agent Coverage': 100,
  'Blackduck Coverage': 100,
  'Veracode Coverage': 100,
  'Coverity Coverage': 100,
  'Criticality Set': 0,
  'Public IP': 0,
  'Commit Signature Verification': 100,
  'CI/CD Success Rate': 85,

  // DORA
  'P75 Lead Time for Changes': 3,
  'Avg Lead Time for Changes': 4,

  // Efficiency & Flow
  'P75 Time to Pickup': 4,
  'Avg Time to Pickup': 14,
  'P75 Time to Review': 14,
  'Avg Time to Review': 20,
  'P75 Time to Merge': 3,
  'Avg Time to Merge': 13,
  'Avg CI/CD duration': 11,
  'P75 PR Size': 155,
  'Avg PR Size': 370,
};

export const kpiDisplayConfig: Record<string, { unit: string; desc: string }> =
  {
    // Security
    'End of Life': {
      unit: '%',
      desc: 'The percentage of resources (OS, Hardware, or Software) still within their manufacturer support date.',
    },
    'Privileged Identity Management': {
      unit: '%',
      desc: 'The percentage of administrative or high-risk accounts that are managed via Just-In-Time (JIT) access rather than having permanent local admin rights.',
    },
    'Key Rotation': {
      unit: '%',
      desc: 'The percentage of cryptographic keys and secrets that have been rotated within the defined policy window.',
    },
    'Agent Coverage': {
      unit: '%',
      desc: 'The percentage of the total fleet that has the required security, logging, and monitoring agents successfully installed and reporting.',
    },
    'Public IP': {
      unit: '#',
      desc: 'The total number of cloud resources currently assigned a Public IP address, representing the external attack surface area.',
    },
    'Blackduck Coverage': {
      unit: '%',
      desc: 'The percentage of identified critical repositories that are onboarded and actively monitored by Black Duck for open source vulnerability detection.',
    },
    'Veracode Coverage': {
      unit: '%',
      desc: 'The percentage of identified critical repositories whose application code is onboarded and analyzed by Veracode for security vulnerabilities.',
    },
    'Coverity Coverage': {
      unit: '%',
      desc: 'The percentage of identified critical repositories that require Coverity, based on their code language, whose application code is onboarded and analyzed by Coverity for security vulnerabilities.',
    },
    'Criticality Set': {
      unit: '#',
      desc: 'The number of repositories that have not been assigned a criticality rating in accordance with organizational standards.',
    },
    'CI/CD Success Rate': {
      unit: '%',
      desc: 'The percentage of CI/CD pipeline runs that completed successfully without errors or failures.',
    },
    'Commit Signature Verification': {
      unit: '%',
      desc: 'The percentage of commits that are cryptographically signed, ensuring code authenticity and integrity.',
    },

    // DORA
    'P75 Lead Time for Changes': {
      unit: 'days',
      desc: 'The 75th percentile of time it takes for a code commit to be successfully running in production.',
    },
    'Avg Lead Time for Changes': {
      unit: 'days',
      desc: 'The average time it takes for a code commit to be successfully running in production.',
    },

    // Efficiency & Flow — P75
    'P75 Time to Pickup': {
      unit: 'hours',
      desc: 'The 75th percentile of time elapsed between a Pull Request being opened and the first human review or comment occurring.',
    },
    'P75 Time to Review': {
      unit: 'hours',
      desc: 'The 75th percentile of time spent in the review cycle, measured from the first review comment until the PR is approved.',
    },
    'P75 Time to Merge': {
      unit: 'hours',
      desc: 'The 75th percentile of time from the initial Pull Request creation until it is successfully merged into the main branch.',
    },

    // Efficiency & Flow — Avg
    'Avg Time to Pickup': {
      unit: 'hours',
      desc: 'The average time elapsed between a Pull Request being opened and the first human review or comment occurring.',
    },
    'Avg Time to Review': {
      unit: 'hours',
      desc: 'The average time spent in the review cycle, measured from the first review comment until the PR is approved.',
    },
    'Avg Time to Merge': {
      unit: 'hours',
      desc: 'The average time from the initial Pull Request creation until it is successfully merged into the main branch.',
    },
    'Avg CI/CD duration': {
      unit: 'min',
      desc: 'The average time taken for a full pipeline execution (Build, Test, and Deploy) to complete across all successful runs.',
    },
    'Avg PR Size': {
      unit: 'LoC',
      desc: 'The average number of Lines of Code (LoC) modified per Pull Request; smaller PRs are generally associated with lower risk and faster reviews.',
    },
  };

export const kpiRouteMap: Record<string, string> = {
  // Security
  'End of Life': '../scorecards?category=End+of+Life',
  'Privileged Identity Management':
    '../scorecards?category=Privileged+Identity+Management',
  'Key Rotation': '../scorecards?category=Key+Rotation',
  'Agent Coverage': '../scorecards?category=Agent+Coverage',
  'Public IP': '../scorecards?category=Public+IP',
  'Blackduck Coverage': '../scorecards?category=Blackduck+Coverage',
  'Veracode Coverage': '../scorecards?category=Veracode+Coverage',
  'Coverity Coverage': '../scorecards?category=Coverity+Coverage',
  'Criticality Set': '../scorecards?category=Criticality+Set',
  'Commit Signature Verification': '../scorecards?category=Signed+Commit',

  // DORA
  'P75 Lead Time for Changes':
    '../engineering-insights?metric=lead-time-for-changes&prod=true&agg=p75',
  'Avg Lead Time for Changes':
    '../engineering-insights?metric=lead-time-for-changes&prod=true&agg=average',

  // Efficiency & Flow
  'P75 Time to Pickup':
    '../engineering-insights?metric=time-to-pickup&prod=true&agg=p75',
  'Avg Time to Pickup':
    '../engineering-insights?metric=time-to-pickup&prod=true&agg=average',
  'P75 Time to Review':
    '../engineering-insights?metric=time-to-review&prod=true&agg=p75',
  'Avg Time to Review':
    '../engineering-insights?metric=time-to-review&prod=true&agg=average',
  'P75 Time to Merge':
    '../engineering-insights?metric=time-to-merge&prod=true&agg=p75',
  'Avg Time to Merge':
    '../engineering-insights?metric=time-to-merge&prod=true&agg=average',
  'Avg CI/CD duration':
    '../engineering-insights?metric=ci-build-duration&agg=average',
  'Avg PR Size': '../engineering-insights?metric=pr-size',
  'CI/CD Success Rate': '../engineering-insights?metric=ci-success-rate',
};

export const getMonthLabels = (
  valueOverTime: { date: string; value: number | string | null }[],
  monthsToShow: number,
): string[] => {
  const allMonths = Array.from(
    new Set(
      valueOverTime.map(v =>
        new Date(v.date).toLocaleString('default', { month: 'short' }),
      ),
    ),
  );
  return allMonths.slice(-monthsToShow);
};

export const parseNumericValue = (
  val: string | number | undefined | null,
): number | null => {
  if (val === undefined || val === null || val === -1) return null;
  if (typeof val === 'number') return val;
  const numeric = parseFloat(val.toString().replace(/[^\d.]/g, ''));
  return isNaN(numeric) ? null : numeric;
};

export const getRAGStatus = (
  label: string,
  value: number | null,
): 'R' | 'A' | 'G' | 'N/A' => {
  if (value === null) return 'N/A';

  const target = normalizedTargetForItem[label];
  if (target === undefined || typeof target === 'string') return 'N/A';

  if (value === target) return 'G';

  const lowerIsBetter = lowerIsBetterKPIs.has(label);

  const denominator = lowerIsBetter ? value : target;
  const numerator = lowerIsBetter ? target : value;

  if (denominator === 0) return 'N/A';

  const percentage = (numerator / denominator) * 100;

  if (percentage > 90) return 'G';
  if (percentage >= 50) return 'A';
  return 'R';
};

export const getRAGColorClass = (
  classes: Record<string, string>,
  ragStatus: 'R' | 'A' | 'G' | 'N/A',
): string => {
  switch (ragStatus) {
    case 'G':
      return classes.cellGreen;
    case 'A':
      return classes.cellAmber;
    case 'R':
      return classes.cellRed;
    case 'N/A':
      return classes.cellNA;
    default:
      return classes.cellGray;
  }
};
